/* Xiaomi 2026 · Partners (Dealers) admin · Sprint 2A · Day 1
 *
 * /xiaomi/partners
 *
 * Features:
 *   • Searchable, filterable list of all 284 partners
 *   • Inline edit any editable field (auto-saves on blur · PATCH)
 *   • Add new partner (modal)
 *   • Soft-delete + restore
 *   • Bulk edit one field on N selected dealers
 *   • CSV Import with preview-before-commit
 *   • CSV Export (respects current filter)
 *   • Field-level audit drawer per partner
 *
 * Visual: matches .xiaomi-theme (paper/orange, Fraunces serif).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Columns shown by default. The full editable set is larger and lives in
// the row-detail drawer.
const TABLE_COLS = [
  { key: 'retailer_id',    label: 'Retailer ID', w: 96,  readonly: true },
  { key: 'outlet_name',    label: 'Outlet',      w: 200 },
  { key: 'owner_name',     label: 'Owner',       w: 160 },
  { key: 'phone_primary',  label: 'Phone',       w: 130 },
  { key: 'tier',           label: 'Tier',        w: 140 },
  { key: 'zone',           label: 'Zone',        w: 120 },
  { key: 'language_pref',  label: 'Lang',        w: 56  },
  { key: 'birthday',       label: 'Birthday',    w: 100 },
  { key: 'anniversary',    label: 'Anniv.',      w: 100 },
];

const TIERS = ['Platinum Luxe', 'Platinum Prime', 'Platinum Signature', 'Rising Platinum'];

export default function XiaomiAdminDealers() {
  const [data, setData] = useState({ rows: [], total: 0, facets: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ tier: '', zone: '', status: '', q: '', include_archived: false });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });
  const [selected, setSelected] = useState(new Set());
  const [activeDrawer, setActiveDrawer] = useState(null);  // {retailer_id} for audit / detail
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showV5Patch, setShowV5Patch] = useState(false);
  const [showEligibility, setShowEligibility] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [busy, setBusy] = useState(false);

  // ── load list ────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => {
        if (v !== '' && v !== false) params.append(k, v);
      });
      params.append('skip', pagination.skip);
      params.append('limit', pagination.limit);
      const r = await axios.get(`${API}/xiaomi/dealers?${params}`, auth());
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [filter, pagination]);

  useEffect(() => { load(); }, [load]);

  // ── inline edit ──────────────────────────────────────────────────
  const patchDealer = async (retailer_id, field, value) => {
    try {
      const r = await axios.patch(
        `${API}/xiaomi/dealers/${retailer_id}`,
        { [field]: value },
        { ...auth(), headers: { ...auth().headers, 'x-edit-source': 'inline' } },
      );
      // Optimistically update the row
      setData((d) => ({
        ...d,
        rows: d.rows.map((row) => row._id === retailer_id ? r.data.dealer : row),
      }));
      if (r.data.audit?.logged?.length) {
        toast.success(`${field} updated · audited`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
      await load();
    }
  };

  // ── soft-delete / restore ────────────────────────────────────────
  const softDelete = async (retailer_id) => {
    if (!window.confirm(`Archive partner ${retailer_id}? You can restore later.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/xiaomi/dealers/${retailer_id}`, auth());
      toast.success('Archived');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    } finally {
      setBusy(false);
    }
  };

  const restore = async (retailer_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/xiaomi/dealers/${retailer_id}/restore`, {}, auth());
      toast.success('Restored');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  // ── CSV export ───────────────────────────────────────────────────
  const exportCsv = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filter.tier)   params.append('tier',   filter.tier);
      if (filter.zone)   params.append('zone',   filter.zone);
      if (filter.status) params.append('status', filter.status);
      if (filter.include_archived) params.append('include_archived', 'true');
      const r = await axios.get(
        `${API}/xiaomi/dealers/export.csv?${params}`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xiaomi_dealers_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadTeamGuide = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/admin/team-guide.docx`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob(
        [r.data],
        { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Xiaomi_2026_Team_Operating_Guide.docx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Team Guide downloaded — share it with the team!');
    } catch (e) {
      toast.error('Could not generate team guide');
    } finally {
      setBusy(false);
    }
  };

  const downloadZohoSpec = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/admin/zoho-field-spec.docx`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob(
        [r.data],
        { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Xiaomi_2026_Zoho_Field_Spec.docx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Zoho field spec downloaded — forward to your Zoho admin');
    } catch (e) {
      toast.error('Could not generate Zoho spec');
    } finally {
      setBusy(false);
    }
  };


  const exportDqCsv = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/dealers/dq-export.csv`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xiaomi_dq_for_RSMs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('DQ CSV downloaded · ready for RSM forwarding');
    } catch (e) {
      toast.error('DQ export failed');
    } finally {
      setBusy(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────
  if (loading && data.rows.length === 0) {
    return <div className="text-soft text-sm">Loading partners…</div>;
  }

  return (
    <div data-testid="xiaomi-admin-dealers">
      <div className="flex items-end justify-between mb-2">
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif' }}>Partners</h1>
          <p className="text-soft text-sm">
            {data.total} of 284 partners · inline edit · CSV in/out · audit log
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost" onClick={exportDqCsv} disabled={busy}
                  data-testid="xiaomi-dealers-export-dq-csv"
                  title="CSV for Anuj/RSMs — every partner + missing required fields + blocked benefits"
                  style={{ background: '#fff3e0', color: '#d98a1f', borderColor: '#f7d9aa' }}>
            ⇩ DQ CSV (for RSMs)
          </button>
          <button className="btn-ghost" onClick={exportCsv} disabled={busy}
                  data-testid="xiaomi-dealers-export-csv">
            ⇩ Export CSV
          </button>
          <button className="btn-ghost" onClick={downloadTeamGuide} disabled={busy}
                  data-testid="xiaomi-dealers-team-guide"
                  title="Download Word doc explaining every Xiaomi screen — share with new joiners"
                  style={{ background: '#eaf6f4', color: '#0F766E', borderColor: '#bfe2dc' }}>
            📘 Team Guide (.docx)
          </button>
          <button className="btn-ghost" onClick={downloadZohoSpec} disabled={busy}
                  data-testid="xiaomi-dealers-zoho-spec"
                  title="Download Zoho field spec for Tanisha to forward to the Zoho admin"
                  style={{ background: '#eef2ff', color: '#3730A3', borderColor: '#c7d2fe' }}>
            🛠 Zoho Spec (Tanisha)
          </button>
          <button className="btn-ghost" onClick={() => setShowImport(true)} disabled={busy}
                  data-testid="xiaomi-dealers-open-import">
            ⇧ Import CSV
          </button>
          <button className="btn-ghost" onClick={() => setShowV5Patch(true)} disabled={busy}
                  data-testid="xiaomi-dealers-open-v5-patch"
                  title="Patch dealers from Xiaomi_2026_Partner_Master_v5.xlsx (dry-run first)"
                  style={{ background: '#fff3e0', color: '#d98a1f', borderColor: '#f7d9aa' }}>
            ⇧ v5 Excel Patch
          </button>
          <button className="btn-ghost" onClick={() => setShowEligibility(true)} disabled={busy}
                  data-testid="xiaomi-dealers-open-eligibility"
                  title="Apply Xiaomi's quarterly eligibility list (upgrade/downgrade/remove/add) · dry-run first"
                  style={{ background: '#ecfdf5', color: '#065F46', borderColor: '#a7f3d0' }}>
            🔄 Eligibility CSV
          </button>
          {selected.size > 0 && (
            <button className="btn-ghost" onClick={() => setShowBulkEdit(true)} disabled={busy}
                    data-testid="xiaomi-dealers-bulk-edit">
              Bulk edit ({selected.size})
            </button>
          )}
          <button className="btn-mi" onClick={() => setShowAdd(true)} disabled={busy}
                  data-testid="xiaomi-dealers-open-add">
            + Add partner
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="x-card-soft p-3 mb-4 flex items-center gap-3 flex-wrap">
        <input
          className="x-input"
          style={{ width: 220, fontSize: 13 }}
          placeholder="Search name / phone / ID..."
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          data-testid="xiaomi-dealers-search"
        />
        <select
          className="x-input" style={{ width: 160, fontSize: 13 }}
          value={filter.tier}
          onChange={(e) => setFilter({ ...filter, tier: e.target.value })}
          data-testid="xiaomi-dealers-filter-tier"
        >
          <option value="">All tiers</option>
          {Object.keys(data.facets.by_tier || {}).map((t) => (
            <option key={t} value={t}>{t} ({data.facets.by_tier[t]})</option>
          ))}
        </select>
        <select
          className="x-input" style={{ width: 160, fontSize: 13 }}
          value={filter.zone}
          onChange={(e) => setFilter({ ...filter, zone: e.target.value })}
          data-testid="xiaomi-dealers-filter-zone"
        >
          <option value="">All zones</option>
          {Object.keys(data.facets.by_zone || {}).map((z) => (
            <option key={z} value={z}>{z} ({data.facets.by_zone[z]})</option>
          ))}
        </select>
        <label className="text-[12px] flex items-center gap-1 text-soft cursor-pointer">
          <input
            type="checkbox"
            checked={filter.include_archived}
            onChange={(e) => setFilter({ ...filter, include_archived: e.target.checked })}
            data-testid="xiaomi-dealers-include-archived"
          />
          show archived
        </label>
        <div className="ml-auto text-[11px] text-soft">
          Showing {data.rows.length} of {data.total}
        </div>
      </div>

      {/* Table */}
      <div className="x-card overflow-auto" data-testid="xiaomi-dealers-table">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr>
              <th style={{ width: 30 }} className="border-b border-line p-2">
                <input
                  type="checkbox"
                  checked={selected.size === data.rows.length && data.rows.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(new Set(data.rows.map((r) => r._id)));
                    else setSelected(new Set());
                  }}
                  data-testid="xiaomi-dealers-select-all"
                />
              </th>
              {TABLE_COLS.map((c) => (
                <th
                  key={c.key}
                  style={{ width: c.w, minWidth: c.w }}
                  className="border-b border-line p-2 text-left text-[10px] uppercase tracking-wider text-soft"
                >
                  {c.label}
                </th>
              ))}
              <th className="border-b border-line p-2 text-[10px] uppercase tracking-wider text-soft" style={{ width: 130 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr
                key={row._id}
                className="border-b border-line-soft last:border-0 hover:bg-[#faf6ef]"
                style={{ opacity: row.is_archived ? 0.5 : 1 }}
              >
                <td className="p-2">
                  <input
                    type="checkbox"
                    checked={selected.has(row._id)}
                    onChange={(e) => {
                      const s = new Set(selected);
                      if (e.target.checked) s.add(row._id); else s.delete(row._id);
                      setSelected(s);
                    }}
                    data-testid={`xiaomi-dealers-select-${row._id}`}
                  />
                </td>
                {TABLE_COLS.map((c) => (
                  <td key={c.key} className="p-2">
                    {c.readonly ? (
                      <span className="text-ink font-mono">{row[c.key] || ''}</span>
                    ) : (
                      <InlineCell
                        value={row[c.key] ?? ''}
                        onSave={(v) => patchDealer(row._id, c.key, v)}
                        testid={`xiaomi-dealers-cell-${row._id}-${c.key}`}
                      />
                    )}
                  </td>
                ))}
                <td className="p-2">
                  <div className="flex gap-1">
                    <a
                      href={`/xiaomi/partners/${row._id}`}
                      className="text-[11px] underline text-[#ff6900] hover:text-[#cc5500] font-semibold"
                      data-testid={`xiaomi-dealers-360-${row._id}`}
                    >
                      360
                    </a>
                    <button
                      type="button"
                      className="text-[11px] underline text-soft hover:text-ink"
                      onClick={() => setActiveDrawer(row._id)}
                      data-testid={`xiaomi-dealers-detail-${row._id}`}
                    >
                      audit
                    </button>
                    {!row.is_archived ? (
                      <button
                        type="button"
                        className="text-[11px] underline text-soft hover:text-[#c0492f]"
                        onClick={() => softDelete(row._id)}
                        data-testid={`xiaomi-dealers-archive-${row._id}`}
                      >
                        archive
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-[11px] underline text-soft hover:text-[#128c7e]"
                        onClick={() => restore(row._id)}
                        data-testid={`xiaomi-dealers-restore-${row._id}`}
                      >
                        restore
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={TABLE_COLS.length + 2} className="p-10 text-center text-soft">
                  No partners match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-[12px]">
        <button
          className="btn-ghost"
          disabled={pagination.skip === 0}
          onClick={() => setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) })}
          data-testid="xiaomi-dealers-prev"
        >
          ← Previous
        </button>
        <div className="text-soft">
          {pagination.skip + 1}–{pagination.skip + data.rows.length} of {data.total}
        </div>
        <button
          className="btn-ghost"
          disabled={pagination.skip + pagination.limit >= data.total}
          onClick={() => setPagination({ ...pagination, skip: pagination.skip + pagination.limit })}
          data-testid="xiaomi-dealers-next"
        >
          Next →
        </button>
      </div>

      {/* Drawers / modals */}
      {activeDrawer && (
        <DealerAuditDrawer
          retailerId={activeDrawer}
          onClose={() => setActiveDrawer(null)}
        />
      )}
      {showAdd && (
        <AddDealerModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
      {showImport && (
        <ImportCsvModal
          onClose={() => setShowImport(false)}
          onCommitted={() => { setShowImport(false); load(); }}
        />
      )}
      {showV5Patch && (
        <V5PatchModal
          onClose={() => setShowV5Patch(false)}
          onApplied={() => { setShowV5Patch(false); load(); }}
        />
      )}
      {showEligibility && (
        <EligibilityModal
          onClose={() => setShowEligibility(false)}
          onApplied={() => { setShowEligibility(false); load(); }}
        />
      )}
      {showBulkEdit && (
        <BulkEditModal
          retailerIds={Array.from(selected)}
          editableFields={data.editable_fields || []}
          onClose={() => setShowBulkEdit(false)}
          onApplied={() => { setShowBulkEdit(false); setSelected(new Set()); load(); }}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────


function InlineCell({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  if (!editing) {
    return (
      <div
        className="cursor-text px-1 py-0.5 rounded hover:bg-[#fff3e6] truncate"
        onClick={() => setEditing(true)}
        title={value || '(click to edit)'}
        style={{ minHeight: 22 }}
        data-testid={testid}
      >
        {value || <span className="text-soft italic">—</span>}
      </div>
    );
  }
  return (
    <input
      autoFocus
      className="x-input"
      style={{ fontSize: 12.5, padding: '2px 6px', height: 26 }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (draft !== value) onSave(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
      data-testid={`${testid}-input`}
    />
  );
}


function DealerAuditDrawer({ retailerId, onClose }) {
  const [rows, setRows] = useState([]);
  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/xiaomi/dealers/${retailerId}/audit?limit=200`, auth()),
          axios.get(`${API}/xiaomi/dealers/${retailerId}`, auth()),
        ]);
        if (!alive) return;
        setRows(a.data.rows);
        setDealer(b.data.dealer);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Audit load failed');
      } finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, [retailerId]);

  return (
    <Overlay onClose={onClose}>
      <div className="flex items-start justify-between p-6 border-b border-line">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-soft">Audit log</div>
          <h2 style={{ fontFamily: 'Fraunces, serif' }} className="mt-1">
            {dealer?.outlet_name || retailerId}
          </h2>
          <div className="text-[12px] text-soft mt-1 font-mono">{retailerId}</div>
        </div>
        <button className="btn-ghost" onClick={onClose}
                data-testid="xiaomi-dealer-audit-close">✕ Close</button>
      </div>
      <div className="overflow-auto flex-1">
        {loading && <div className="p-6 text-soft text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center text-soft text-sm">
            No edits recorded yet for this partner.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-[12px]">
            <thead>
              <tr>
                {['When', 'Field', 'Before', 'After', 'By', 'Source'].map((h) => (
                  <th key={h} className="bg-panel2 sticky top-0 p-2 text-left text-[10px] uppercase tracking-wider text-soft border-b border-line">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-line-soft last:border-0">
                  <td className="p-2 text-soft" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(r.ts).toLocaleString()}
                  </td>
                  <td className="p-2 font-mono text-[11px]">{r.field}</td>
                  <td className="p-2" style={{ wordBreak: 'break-word' }}>
                    <code className="text-[11px] text-soft">{stringify(r.before)}</code>
                  </td>
                  <td className="p-2" style={{ wordBreak: 'break-word' }}>
                    <code className="text-[11px]" style={{ color: '#ff6900' }}>{stringify(r.after)}</code>
                  </td>
                  <td className="p-2 text-soft text-[11px]">{r.actor_email}</td>
                  <td className="p-2">
                    <span className="chip" style={{ fontSize: 9.5 }}>{r.source}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Overlay>
  );
}


function AddDealerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    retailer_id: '', outlet_name: '', owner_name: '', phone_primary: '',
    email_primary: '', tier: 'Platinum Prime', zone: '', city: '', state: '',
    language_pref: 'en', birthday: '', anniversary: '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.retailer_id || !form.outlet_name) {
      toast.error('retailer_id + outlet_name are required'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/xiaomi/dealers`, form, auth());
      toast.success(`Partner ${form.retailer_id} created · audited`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} narrow>
      <div className="p-6 border-b border-line">
        <h2 style={{ fontFamily: 'Fraunces, serif' }}>Add a partner</h2>
        <p className="text-soft text-sm">All fields can be edited later. Min: retailer_id + outlet_name.</p>
      </div>
      <div className="p-6 grid grid-cols-2 gap-3 overflow-auto flex-1">
        {[
          ['retailer_id', 'Retailer ID'],
          ['outlet_name', 'Outlet name'],
          ['owner_name', 'Owner name'],
          ['phone_primary', 'Phone'],
          ['email_primary', 'Email'],
          ['city', 'City'],
          ['state', 'State'],
          ['birthday', 'Birthday (e.g. 19 Feb)'],
          ['anniversary', 'Anniversary (e.g. 11 Feb)'],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="text-[11px] text-soft uppercase tracking-wider">{label}</label>
            <input
              className="x-input w-full"
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`xiaomi-add-${k}`}
            />
          </div>
        ))}
        <div>
          <label className="text-[11px] text-soft uppercase tracking-wider">Tier</label>
          <select
            className="x-input w-full"
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value })}
            data-testid="xiaomi-add-tier"
          >
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-soft uppercase tracking-wider">Zone</label>
          <input
            className="x-input w-full"
            value={form.zone}
            onChange={(e) => setForm({ ...form, zone: e.target.value })}
            data-testid="xiaomi-add-zone"
          />
        </div>
        <div>
          <label className="text-[11px] text-soft uppercase tracking-wider">Language</label>
          <input
            className="x-input w-full"
            value={form.language_pref}
            onChange={(e) => setForm({ ...form, language_pref: e.target.value })}
            data-testid="xiaomi-add-language"
          />
        </div>
      </div>
      <div className="p-4 border-t border-line flex justify-end gap-2 bg-panel2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-mi" onClick={submit} disabled={busy} data-testid="xiaomi-add-submit">
          {busy ? 'Saving…' : 'Add partner'}
        </button>
      </div>
    </Overlay>
  );
}


function ImportCsvModal({ onClose, onCommitted }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    if (!file) { toast.error('Pick a CSV first'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/xiaomi/dealers/import-csv/preview`, fd, auth(),
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!preview?.staging_id) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/dealers/import-csv/commit`,
        { staging_id: preview.staging_id }, auth(),
      );
      toast.success(`Committed · ${r.data.updated} updated · ${r.data.added} added · ${r.data.field_audit_rows} audit rows`);
      onCommitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Commit failed');
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="p-6 border-b border-line">
        <h2 style={{ fontFamily: 'Fraunces, serif' }}>Import partners from CSV</h2>
        <p className="text-soft text-sm">
          Header row required. Match key is <code>retailer_id</code>. Empty cells = "don't change".
          Use <code>&lt;BLANK&gt;</code> to explicitly clear a field.
        </p>
      </div>
      <div className="p-6 overflow-auto flex-1 space-y-4">
        <input
          type="file"
          accept=".csv"
          onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); }}
          data-testid="xiaomi-import-file-input"
        />
        {!preview && (
          <button className="btn-mi" onClick={doPreview} disabled={busy || !file}
                  data-testid="xiaomi-import-preview-btn">
            {busy ? 'Parsing…' : 'Preview diff'}
          </button>
        )}
        {preview && (
          <div className="space-y-3" data-testid="xiaomi-import-preview-result">
            <div className="grid grid-cols-4 gap-2">
              {[
                ['Updates',  preview.stats.will_update, '#fff3e0', '#d98a1f'],
                ['Adds',     preview.stats.will_add,    '#dcf8e8', '#128c7e'],
                ['Skips',    preview.stats.will_skip,   '#f0ece3', '#6b6155'],
                ['Errors',   preview.stats.errors,      '#fbe9e3', '#c0492f'],
              ].map(([label, n, bg, fg]) => (
                <div key={label} className="x-card-soft p-3 text-center" style={{ background: bg, borderColor: bg }}>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: fg }}>{label}</div>
                  <div className="num-display" style={{ color: fg, fontSize: 26 }}>{n}</div>
                </div>
              ))}
            </div>
            {preview.warnings && preview.warnings.length > 0 && (
              <div className="x-card-soft p-3 text-[12px]" style={{ background: '#fff3e0' }}>
                <strong>Warnings:</strong>
                <ul className="list-disc pl-5">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            {preview.errors?.length > 0 && (
              <details className="x-card-soft p-3 text-[12px]" style={{ background: '#fbe9e3' }}>
                <summary><strong>Errors ({preview.errors.length})</strong></summary>
                <ul className="list-disc pl-5 mt-2">
                  {preview.errors.slice(0, 50).map((e, i) => (
                    <li key={i}>row {e.row} · {e.retailer_id || '?'} · {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
            {preview.will_update?.length > 0 && (
              <details className="x-card-soft p-3 text-[12px]">
                <summary><strong>Updates ({preview.will_update.length})</strong> — review before commit</summary>
                <table className="w-full mt-2 text-[11px]">
                  <thead>
                    <tr>
                      <th className="text-left">Retailer</th>
                      <th className="text-left">Outlet</th>
                      <th className="text-left">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.will_update.slice(0, 100).map((u, i) => (
                      <tr key={i} className="border-t border-line-soft">
                        <td className="font-mono">{u.retailer_id}</td>
                        <td>{u.outlet_name}</td>
                        <td>
                          {u.diff.map((d) => (
                            <div key={d.field}>
                              <strong>{d.field}</strong>:{' '}
                              <code className="text-soft">{stringify(d.before)}</code>{' → '}
                              <code style={{ color: '#ff6900' }}>{stringify(d.after)}</code>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
            {preview.will_add?.length > 0 && (
              <details className="x-card-soft p-3 text-[12px]">
                <summary><strong>New partners ({preview.will_add.length})</strong></summary>
                <ul className="list-disc pl-5 mt-2">
                  {preview.will_add.map((a, i) => (
                    <li key={i}>{a.retailer_id} · {a.new_fields.outlet_name || a.new_fields.owner_name || '?'}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
      <div className="p-4 border-t border-line flex justify-end gap-2 bg-panel2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        {preview && (
          <button className="btn-dark" onClick={doCommit} disabled={busy}
                  data-testid="xiaomi-import-commit-btn">
            {busy ? 'Committing…' : `Confirm · apply ${preview.stats.will_update + preview.stats.will_add} changes`}
          </button>
        )}
      </div>
    </Overlay>
  );
}


function BulkEditModal({ retailerIds, editableFields, onClose, onApplied }) {
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!field) { toast.error('Pick a field'); return; }
    if (!window.confirm(`Set ${field} = "${value}" on ${retailerIds.length} partners? Audited.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/xiaomi/dealers/bulk-update`, {
        retailer_ids: retailerIds, field, value, note,
      }, auth());
      toast.success(`Updated ${r.data.updated} · audit ${r.data.audit_rows}`);
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
    } finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} narrow>
      <div className="p-6 border-b border-line">
        <h2 style={{ fontFamily: 'Fraunces, serif' }}>Bulk edit {retailerIds.length} partner{retailerIds.length === 1 ? '' : 's'}</h2>
        <p className="text-soft text-sm">Set one field on all selected partners. Every change is audit-logged.</p>
      </div>
      <div className="p-6 space-y-3">
        <div>
          <label className="text-[11px] text-soft uppercase tracking-wider">Field</label>
          <select
            className="x-input w-full"
            value={field} onChange={(e) => setField(e.target.value)}
            data-testid="xiaomi-bulk-field"
          >
            <option value="">— pick a field —</option>
            {editableFields.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-soft uppercase tracking-wider">New value</label>
          <input
            className="x-input w-full"
            value={value} onChange={(e) => setValue(e.target.value)}
            data-testid="xiaomi-bulk-value"
          />
        </div>
        <div>
          <label className="text-[11px] text-soft uppercase tracking-wider">Note (shown in audit)</label>
          <input
            className="x-input w-full"
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="optional"
            data-testid="xiaomi-bulk-note"
          />
        </div>
      </div>
      <div className="p-4 border-t border-line flex justify-end gap-2 bg-panel2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn-mi" onClick={submit} disabled={busy} data-testid="xiaomi-bulk-submit">
          Apply to {retailerIds.length}
        </button>
      </div>
    </Overlay>
  );
}


function V5PatchModal({ onClose, onApplied }) {
  const [v5File, setV5File] = useState(null);
  const [verifyFile, setVerifyFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [archiveMissing, setArchiveMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const buildForm = (dryRun) => {
    const fd = new FormData();
    fd.append('file', v5File);
    if (verifyFile) fd.append('verify_file', verifyFile);
    fd.append('dry_run', dryRun ? 'true' : 'false');
    fd.append('archive_missing', archiveMissing ? 'true' : 'false');
    return fd;
  };

  const doPreview = async () => {
    if (!v5File) { toast.error('Pick the v5 .xlsx first'); return; }
    setBusy(true); setPreview(null);
    try {
      const r = await axios.post(
        `${API}/xiaomi/admin/patch-v5`, buildForm(true), auth(),
      );
      setPreview(r.data.report);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!preview) return;
    const fc = preview.counts.field_diffs;
    const arc = archiveMissing ? preview.counts.missing_from_v5 : 0;
    if (!window.confirm(
      `Apply ${fc} field change${fc === 1 ? '' : 's'}`
      + (arc ? ` and archive ${arc} missing partner${arc === 1 ? '' : 's'}` : '')
      + `?\nThis cannot be undone (changes are audit-logged).`,
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/admin/patch-v5`, buildForm(false), auth(),
      );
      const ap = r.data.report.applied;
      toast.success(
        `Patch applied · ${ap.dealers_updated} dealers · ${ap.fields_set} fields`
        + (ap.archived ? ` · ${ap.archived} archived` : ''),
      );
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  // Group changes by field for a tidy summary
  const byField = useMemo(() => {
    if (!preview?.changes) return [];
    const m = {};
    preview.changes.forEach((c) => {
      if (c.field === '_NEW_INSERT_') return;
      (m[c.field] ||= []).push(c);
    });
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
  }, [preview]);

  return (
    <Overlay onClose={onClose}>
      <div className="p-6 border-b border-line">
        <h2 style={{ fontFamily: 'Fraunces, serif' }}>v5 Excel patch · safe diff & apply</h2>
        <p className="text-soft text-sm">
          Upload <code>Xiaomi_2026_Partner_Master_v5.xlsx</code> and (optionally) the
          ZH/ZSM mobile verification xlsx. Guardrails: never nulls populated fields ·
          owner_name pollution stripped · ZH/ZSM mobile sourced from verification file
          only (v5 mobile columns ignored).
        </p>
      </div>

      <div className="p-6 overflow-auto flex-1 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] text-soft uppercase tracking-wider">
              v5 Master xlsx <span style={{ color: '#c0492f' }}>*</span>
            </label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => { setV5File(e.target.files?.[0] || null); setPreview(null); }}
              data-testid="xiaomi-v5patch-file-input"
            />
          </div>
          <div>
            <label className="text-[11px] text-soft uppercase tracking-wider">
              ZH/ZSM Verification xlsx <span className="text-soft">(optional)</span>
            </label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => { setVerifyFile(e.target.files?.[0] || null); setPreview(null); }}
              data-testid="xiaomi-v5patch-verify-input"
            />
          </div>
        </div>

        {!preview && (
          <button className="btn-mi" onClick={doPreview} disabled={busy || !v5File}
                  data-testid="xiaomi-v5patch-preview-btn">
            {busy ? 'Diffing…' : 'Preview diff (dry-run)'}
          </button>
        )}

        {preview && (
          <div className="space-y-3" data-testid="xiaomi-v5patch-preview-result">
            <div className="grid grid-cols-5 gap-2">
              {[
                ['DB dealers', preview.counts.db_dealers, '#f0ece3', '#6b6155'],
                ['v5 rows', preview.counts.v5_rows, '#f0ece3', '#6b6155'],
                ['Field diffs', preview.counts.field_diffs, '#fff3e0', '#d98a1f'],
                ['Missing in v5', preview.counts.missing_from_v5, '#fbe9e3', '#c0492f'],
                ['New in v5', preview.counts.new_in_v5, '#dcf8e8', '#128c7e'],
              ].map(([label, n, bg, fg]) => (
                <div key={label} className="x-card-soft p-3 text-center"
                     style={{ background: bg, borderColor: bg }}>
                  <div className="text-[10px] uppercase tracking-wider"
                       style={{ color: fg }}>{label}</div>
                  <div className="num-display" style={{ color: fg, fontSize: 24 }}>{n}</div>
                </div>
              ))}
            </div>

            {preview.counts.blank_protect_skips > 0 && (
              <div className="x-card-soft p-3 text-[12px]" style={{ background: '#fff3e0' }}>
                <strong>{preview.counts.blank_protect_skips}</strong> v5 blank values
                were ignored to protect populated DB fields. (Use Bulk Edit to clear
                a field explicitly.)
              </div>
            )}

            {!preview.verify_file_used && verifyFile && (
              <div className="x-card-soft p-3 text-[12px]" style={{ background: '#fbe9e3' }}>
                Verification file was provided but no rows were mapped — header detection failed.
              </div>
            )}

            {preview.missing_from_v5?.length > 0 && (
              <details className="x-card-soft p-3 text-[12px]" open>
                <summary>
                  <strong>Missing from v5 ({preview.missing_from_v5.length})</strong>
                  {' '}— candidates for archive
                </summary>
                <ul className="list-disc pl-5 mt-2">
                  {preview.missing_from_v5.map((m) => (
                    <li key={m.retailer_id}>
                      <span className="font-mono">{m.retailer_id}</span>
                      {' · '}{m.outlet_name}
                      {' · '}<span className="text-soft">{m.tier}</span>
                      {' · '}<span className="text-soft">{m.status_label}</span>
                    </li>
                  ))}
                </ul>
                <label className="mt-3 flex items-center gap-2 text-[12px]"
                       style={{ color: '#c0492f' }}>
                  <input
                    type="checkbox"
                    checked={archiveMissing}
                    onChange={(e) => setArchiveMissing(e.target.checked)}
                    data-testid="xiaomi-v5patch-archive-missing"
                  />
                  Archive these {preview.missing_from_v5.length} partner
                  {preview.missing_from_v5.length === 1 ? '' : 's'} on apply
                </label>
              </details>
            )}

            {byField.length > 0 && (
              <details className="x-card-soft p-3 text-[12px]" open>
                <summary><strong>Field diffs ({preview.counts.field_diffs})</strong></summary>
                <ul className="list-disc pl-5 mt-2 mb-3">
                  {byField.map(([f, list]) => (
                    <li key={f}><strong>{f}</strong>: {list.length}
                      {list[0]?.src === 'verify' && (
                        <span className="text-soft"> · from verify file</span>
                      )}
                    </li>
                  ))}
                </ul>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr>
                      <th className="text-left">Retailer</th>
                      <th className="text-left">Outlet</th>
                      <th className="text-left">Field</th>
                      <th className="text-left">Before</th>
                      <th className="text-left">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.changes
                      .filter((c) => c.field !== '_NEW_INSERT_')
                      .slice(0, showAll ? 9999 : 60)
                      .map((c, i) => (
                      <tr key={i} className="border-t border-line-soft">
                        <td className="font-mono">{c.dealer_id}</td>
                        <td>{c.outlet_name}</td>
                        <td><strong>{c.field}</strong>
                          {c.src === 'verify' && (
                            <span style={{ color: '#128c7e' }}> ⓥ</span>
                          )}
                        </td>
                        <td><code className="text-soft">{stringify(c.old)}</code></td>
                        <td><code style={{ color: '#ff6900' }}>{stringify(c.new)}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!showAll && preview.changes.length > 60 && (
                  <button className="btn-ghost mt-2"
                          onClick={() => setShowAll(true)}
                          data-testid="xiaomi-v5patch-show-all">
                    Show all {preview.changes.length} changes
                  </button>
                )}
              </details>
            )}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-line flex justify-end gap-2 bg-panel2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        {preview && (
          <>
            <button className="btn-ghost" onClick={doPreview} disabled={busy}>
              Re-run preview
            </button>
            <button className="btn-dark" onClick={doApply}
                    disabled={busy || (preview.counts.field_diffs === 0
                                       && !(archiveMissing && preview.counts.missing_from_v5 > 0))}
                    data-testid="xiaomi-v5patch-apply-btn">
              {busy ? 'Applying…' : `Apply ${preview.counts.field_diffs} change${
                preview.counts.field_diffs === 1 ? '' : 's'}`
                + (archiveMissing && preview.counts.missing_from_v5
                    ? ` + archive ${preview.counts.missing_from_v5}` : '')}
            </button>
          </>
        )}
      </div>
    </Overlay>
  );
}


function EligibilityModal({ onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imports, setImports] = useState([]);
  const [showImports, setShowImports] = useState(false);

  const loadImports = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/xiaomi/eligibility/imports?limit=20`, auth());
      setImports(r.data.imports || []);
    } catch (e) {
      // silent — ledger is informational
    }
  }, []);

  useEffect(() => { loadImports(); }, [loadImports]);

  const upload = async (dryRun) => {
    if (!file) { toast.error('Pick the eligibility CSV first'); return; }
    setBusy(true);
    if (dryRun) setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/xiaomi/eligibility/import?dry_run=${dryRun}`,
        fd, auth(),
      );
      if (dryRun) {
        setPreview(r.data);
      } else {
        const a = r.data.applied || {};
        toast.success(
          `Applied · ${a.tier_updated || 0} tier changes · ${a.archived || 0} archived · ${a.created || 0} new · ${a.noop || 0} no-op`,
        );
        loadImports();
        onApplied();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || (dryRun ? 'Preview failed' : 'Apply failed'));
    } finally { setBusy(false); }
  };

  const doApply = () => {
    if (!preview) return;
    const c = preview.counts;
    const total = (c.would_update || 0) + (c.would_archive || 0) + (c.would_create || 0);
    if (total === 0) {
      toast.info('Nothing to apply — every row is a no-op.');
      return;
    }
    if (!window.confirm(
      `Apply ${c.would_update || 0} tier change${c.would_update === 1 ? '' : 's'}`
      + (c.would_archive ? ` + ${c.would_archive} archive${c.would_archive === 1 ? '' : 's'}` : '')
      + (c.would_create ? ` + ${c.would_create} new partner${c.would_create === 1 ? '' : 's'}` : '')
      + `?\nThis cannot be undone (changes are audit-logged).`,
    )) return;
    upload(false);
  };

  const downloadTemplate = () => {
    window.open(`${API}/xiaomi/eligibility/template.csv?token=${localStorage.getItem('access_token') || ''}`, '_blank');
  };
  const downloadCurrent = () => {
    window.open(`${API}/xiaomi/eligibility/export.csv?token=${localStorage.getItem('access_token') || ''}`, '_blank');
  };

  return (
    <Overlay onClose={onClose}>
      <div className="p-6 border-b border-line">
        <h2 style={{ fontFamily: 'Fraunces, serif' }}>Quarterly eligibility · import & export</h2>
        <p className="text-soft text-sm">
          Apply Xiaomi's quarterly list of upgrades, downgrades, and removals via CSV.
          Each row: <code>retailer_id, action, new_tier, reason</code>. Actions:
          {' '}<code>upgrade</code> · <code>downgrade</code> · <code>remove</code> · <code>add</code>.
          Idempotent — re-running the same file is a no-op.
        </p>
      </div>

      <div className="p-6 overflow-auto flex-1 space-y-4">
        {/* Round-trip helpers */}
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost" onClick={downloadCurrent} disabled={busy}
                  data-testid="xiaomi-eligibility-export-current"
                  title="Download every active dealer with current tier — edit in Excel, then re-upload">
            ⇩ Export current state
          </button>
          <button className="btn-ghost" onClick={downloadTemplate} disabled={busy}
                  data-testid="xiaomi-eligibility-download-template"
                  title="Blank CSV with 4 example rows · one per action">
            ⇩ Blank template
          </button>
          <button className="btn-ghost" onClick={() => setShowImports(!showImports)} disabled={busy}
                  data-testid="xiaomi-eligibility-toggle-history">
            {showImports ? '✕ Hide' : '⌚'} Past imports ({imports.length})
          </button>
        </div>

        {showImports && (
          <div className="x-card-soft p-3 text-[12px] max-h-48 overflow-auto"
               data-testid="xiaomi-eligibility-history">
            {imports.length === 0 ? (
              <div className="text-soft italic">No imports yet.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase text-soft">
                    <th className="text-left">When</th>
                    <th className="text-left">By</th>
                    <th className="text-left">File</th>
                    <th className="text-right">Updated</th>
                    <th className="text-right">Archived</th>
                    <th className="text-right">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((im, i) => (
                    <tr key={i} className="border-t border-line">
                      <td>{new Date(im.applied_at).toLocaleString()}</td>
                      <td className="text-soft">{im.applied_by}</td>
                      <td className="font-mono text-[11px]">{im.filename}</td>
                      <td className="text-right">{im.applied?.tier_updated || 0}</td>
                      <td className="text-right">{im.applied?.archived || 0}</td>
                      <td className="text-right">{im.applied?.created || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Upload */}
        <div>
          <label className="text-[11px] text-soft uppercase tracking-wider">
            Quarterly eligibility CSV <span style={{ color: '#c0492f' }}>*</span>
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            data-testid="xiaomi-eligibility-file-input"
          />
        </div>

        {!preview && (
          <button className="btn-mi" onClick={() => upload(true)} disabled={busy || !file}
                  data-testid="xiaomi-eligibility-preview-btn">
            {busy ? 'Diffing…' : 'Preview diff (dry-run)'}
          </button>
        )}

        {preview && (
          <div className="space-y-3" data-testid="xiaomi-eligibility-preview-result">
            <div className="grid grid-cols-6 gap-2">
              {[
                ['Rows',     preview.counts.rows,          '#f0ece3', '#6b6155'],
                ['Updates',  preview.counts.would_update,  '#fff3e0', '#d98a1f'],
                ['Archive',  preview.counts.would_archive, '#fbe9e3', '#c0492f'],
                ['New',      preview.counts.would_create,  '#dcf8e8', '#128c7e'],
                ['No-op',    (preview.counts.noop_matches || 0)
                              + (preview.counts.noop_archived || 0)
                              + (preview.counts.noop_exists || 0), '#f0ece3', '#6b6155'],
                ['Errors',   preview.counts.errors,        '#fbe9e3', '#c0492f'],
              ].map(([label, n, bg, fg]) => (
                <div key={label} className="x-card-soft p-3 text-center"
                     style={{ background: bg, borderColor: bg }}>
                  <div className="text-[10px] uppercase tracking-wider"
                       style={{ color: fg }}>{label}</div>
                  <div className="num-display" style={{ color: fg, fontSize: 22 }}>{n}</div>
                </div>
              ))}
            </div>

            {preview.errors?.length > 0 && (
              <details className="x-card-soft p-3 text-[12px]" open
                       style={{ background: '#fbe9e3', borderColor: '#f5c6c6' }}>
                <summary>
                  <strong style={{ color: '#c0492f' }}>
                    Errors ({preview.errors.length}) — fix the CSV before applying
                  </strong>
                </summary>
                <ul className="list-disc pl-5 mt-2 max-h-48 overflow-auto">
                  {preview.errors.slice(0, 200).map((e, i) => (
                    <li key={i}>
                      <span className="font-mono">row {e.row}</span>: {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {preview.plan?.filter((p) => p.result.startsWith('would_')).length > 0 && (
              <details className="x-card-soft p-3 text-[12px]" open>
                <summary>
                  <strong>Changes to apply ({preview.plan.filter((p) => p.result.startsWith('would_')).length})</strong>
                </summary>
                <table className="w-full text-[11px] mt-2">
                  <thead>
                    <tr className="text-[10px] uppercase text-soft">
                      <th className="text-left">Retailer</th>
                      <th className="text-left">Outlet</th>
                      <th className="text-left">Action</th>
                      <th className="text-left">From → To</th>
                      <th className="text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.plan.filter((p) => p.result.startsWith('would_')).slice(0, 200).map((p, i) => (
                      <tr key={i} className="border-t border-line">
                        <td className="font-mono">{p.retailer_id}</td>
                        <td>{p.outlet || '—'}</td>
                        <td>
                          <span className="px-1.5 py-0.5 rounded text-[10px]"
                                style={{
                                  background: p.action === 'remove' ? '#fbe9e3'
                                            : p.action === 'add'    ? '#dcf8e8'
                                            : '#fff3e0',
                                  color:      p.action === 'remove' ? '#c0492f'
                                            : p.action === 'add'    ? '#128c7e'
                                            : '#d98a1f',
                                }}>{p.action}</span>
                        </td>
                        <td>
                          {p.result === 'would_archive' ? '→ archive'
                            : p.result === 'would_create' ? `→ ${p.new_tier}`
                            : `${p.current_tier || '?'} → ${p.new_tier}`}
                        </td>
                        <td className="text-soft">{p.reason || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {(preview.counts.noop_matches || preview.counts.noop_archived || preview.counts.noop_exists) > 0 && (
              <div className="x-card-soft p-3 text-[12px]" style={{ background: '#f0ece3' }}>
                <strong>{(preview.counts.noop_matches || 0)
                          + (preview.counts.noop_archived || 0)
                          + (preview.counts.noop_exists || 0)}</strong>{' '}
                row{((preview.counts.noop_matches || 0) + (preview.counts.noop_archived || 0)
                      + (preview.counts.noop_exists || 0)) === 1 ? '' : 's'} already match the desired state (no-op).
              </div>
            )}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-line flex justify-end gap-2 bg-panel2">
        <button className="btn-ghost" onClick={onClose} disabled={busy}>Close</button>
        {preview && (
          <>
            <button className="btn-ghost" onClick={() => setPreview(null)} disabled={busy}>
              ← Re-preview
            </button>
            <button className="btn-mi" onClick={doApply}
                    disabled={busy || preview.errors?.length > 0
                              || ((preview.counts.would_update || 0)
                                  + (preview.counts.would_archive || 0)
                                  + (preview.counts.would_create || 0)) === 0}
                    data-testid="xiaomi-eligibility-apply-btn">
              {busy ? 'Applying…'
                : preview.errors?.length > 0 ? `Fix ${preview.errors.length} error${preview.errors.length === 1 ? '' : 's'} first`
                : `Apply ${(preview.counts.would_update || 0)
                          + (preview.counts.would_archive || 0)
                          + (preview.counts.would_create || 0)} change${
                          ((preview.counts.would_update || 0)
                            + (preview.counts.would_archive || 0)
                            + (preview.counts.would_create || 0)) === 1 ? '' : 's'}`}
            </button>
          </>
        )}
      </div>
    </Overlay>
  );
}


function Overlay({ children, onClose, narrow }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      style={{ background: 'rgba(20,15,8,0.45)' }}
      onClick={onClose}
    >
      <div
        className="x-modal flex flex-col w-full h-full"
        style={{ maxWidth: narrow ? 540 : 980, borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}


function stringify(v) {
  if (v === null || v === undefined) return '∅';
  if (v === '') return '""';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
